"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, Globe } from "lucide-react";

export default function WebsiteDomainScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Custom domain on Plus and Pro", "Dominio custom en Plus y Pro")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Free subdomain. ", "Subdominio gratis. ")}
            <span className="text-redy-charcoal/30">{t("Your domain on Pro.", "Tu dominio en Pro.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Start free on yourname.useredy.com. When you're ready, connect your own domain: Redy shows you the two DNS records to add, and SSL is automatic.",
              "Empieza gratis en tunombre.useredy.com. Cuando quieras, conectas tu propio dominio: Redy te muestra los dos registros DNS que agregar, y el SSL es automático."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[860px] mx-auto"
        >
          {/* Free tier */}
          <div className="bg-white border border-black/5 rounded-[24px] p-7 shadow-[0_20px_60px_rgba(0,0,0,0.04)]">
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-4">
              {t("Free · Redy subdomain", "Gratis · Subdominio Redy")}
            </div>

            <div className="bg-[#FAFAF8] rounded-[14px] px-4 py-3 mb-5 flex items-center gap-2.5">
              <Globe className="w-4 h-4 text-redy-secondary/55" />
              <div className="font-bold text-redy-charcoal text-[15px] tracking-tight" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                <span className="text-redy-secondary/55">pepitos</span>
                <span className="text-redy-charcoal">.useredy.com</span>
              </div>
            </div>

            <div className="space-y-2">
              {[
                t("SSL included", "SSL incluido"),
                t("Global CDN", "CDN global"),
                t("Auto menu sync", "Auto sync del menú"),
                t("Google-indexed", "Indexado por Google"),
              ].map((feat) => (
                <div key={feat} className="flex items-center gap-2 text-[13px]">
                  <Check className="w-3.5 h-3.5" style={{ color: "#2ECC71" }} strokeWidth={3} />
                  <span className="text-redy-charcoal/75">{feat}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-black/5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/60">
                {t("Cost", "Costo")}
              </div>
              <div className="text-[28px] font-black text-redy-charcoal tabular-nums">$0</div>
            </div>
          </div>

          {/* Pro tier with custom domain */}
          <div
            className="rounded-[24px] p-7 shadow-2xl text-white relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #1A1A1A 0%, #2A1A1A 100%)",
            }}
          >
            <div
              className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(244,114,43,0.18) 0%, transparent 65%)" }}
            />
            <div className="relative">
              <div className="text-[10px] font-black tracking-widest uppercase mb-4" style={{ color: "#F4722B" }}>
                {t("Plus & Pro · Custom domain", "Plus y Pro · Dominio custom")}
              </div>

              <div className="bg-white/10 backdrop-blur border border-white/15 rounded-[14px] px-4 py-3 mb-5 flex items-center gap-2.5">
                <Globe className="w-4 h-4" style={{ color: "#F4722B" }} />
                <div
                  className="font-bold text-[15px] tracking-tight"
                  style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                >
                  <span className="text-white">pepitosdallas.com</span>
                </div>
              </div>

              <div className="space-y-2">
                {[
                  t("Everything in Free", "Todo lo de Gratis"),
                  t("Custom domain (yourname.com)", "Dominio custom (tudominio.com)"),
                  t("Step-by-step DNS records", "Registros DNS paso a paso"),
                  t("Old site links land on your page", "Los links del sitio viejo llegan a tu página"),
                ].map((feat) => (
                  <div key={feat} className="flex items-center gap-2 text-[13px]">
                    <Check className="w-3.5 h-3.5" style={{ color: "#F4722B" }} strokeWidth={3} />
                    <span className="text-white/85">{feat}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-white/10">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/55">
                  {t("Cost", "Costo")}
                </div>
                <div className="text-[28px] font-black text-white tabular-nums">
                  <span className="text-[14px] font-bold text-white/55">{t("From ", "Desde ")}</span>$49<span className="text-[14px] font-bold text-white/55">/mo</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
